/**
 * ModernWash 레거시 "미러" 조회 API — 분리된 "ModernWash 운영" 대시보드 전용.
 *
 * ⚠️ 라이브 어댑터(`owner-console-v2/api/modernwash.js`, `/owner-console/modernwash/*`)와 다르다.
 *   - 라이브 어댑터: V1 API 를 HTTP 로 실시간 호출 → { enabled, source:{reachable,degraded} } 봉투.
 *   - 미러(여기):   legacy_mw_* 스냅샷만 읽는다 → { service, scope, freshness, reason, data } 봉투.
 *   이 파일은 **절대 라이브 어댑터를 호출하지 않는다.** 미러 화면은 미러만 읽는다.
 *
 * 경로: GET /owner-console/modernwash/mirror/*  (axios baseURL 이 이미 /api/v1 을 붙인다)
 *   → 실제 백엔드 컨트롤러: @Controller('api/v1/owner-console/modernwash/mirror')
 *
 * 스코프 규칙(라이브 어댑터와 동일):
 *   master  는 선택 storeId 를 함께 보낸다.
 *   manager 는 storeId 를 보내지 않는다 — 백엔드 StoreScopeResolver 가 본인 매장으로 강제한다.
 *   (클라이언트가 storeId 를 보내도 비-master 에선 무시된다.)
 *
 * 응답 봉투(모든 엔드포인트 공통, dto/modernwash-legacy-mirror.dto.ts):
 *   { service:'modernwash'|'mogoo', scope:{role,storeId,storeName,storeCode,linkedToModernwash}|null,
 *     freshness:{ activeSyncRunId, lastSuccessfulAt, latestRunId, latestRunStatus, freshnessStatus, ageSeconds },
 *     reason: string|null, data: T|null }
 *
 * 기존 관리자 api client 재사용 (Bearer 자동 첨부 + 401 → /login 리다이렉트).
 */
import api from './client';

/** 미러 컨트롤러 하위 네임스페이스 (라이브 어댑터 `/owner-console/modernwash` 와 경로가 겹치지 않는다). */
const MIRROR_BASE = '/owner-console/modernwash/mirror';

/** undefined/null/'' 파라미터는 제거해서 보낸다(빈 값으로 400 나지 않게). */
const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  );

/**
 * 분류 체계 — 카테고리 필터의 단일 소스(백엔드 config). 프론트에서 카테고리를 하드코딩하지 않는다.
 * service 미지정 시 두 서비스(modernwash/mogoo) 모두 반환.
 * 반환: { services: [{ service, origin, servedHere, categories:[{key,label,group,overlapping,countedInActualWash,description}] }] }
 */
export async function getMirrorTaxonomy(service) {
  const res = await api.get(`${MIRROR_BASE}/taxonomy`, { params: cleanParams({ service }) });
  return res.data;
}

/** 신선도 배너 — activeSyncRunId / lastSuccessfulAt / freshnessStatus 등. data 는 null(봉투의 freshness 만 쓴다). */
export async function getMirrorFreshness(params = {}) {
  const res = await api.get(`${MIRROR_BASE}/freshness`, { params: cleanParams(params) });
  return res.data;
}

/** 요약 — 회원/차량/구독 수 + 실 세차(actualWashCount) + 분류 세부(washBreakdown) + 결제 참고치 + 섹션별 상태. */
export async function getMirrorSummary(params = {}) {
  const res = await api.get(`${MIRROR_BASE}/summary`, { params: cleanParams(params) });
  return res.data;
}

/** 회원 미러 목록 — 표시명/상태/차량 수만; email·phone 은 API 계약상 반환하지 않는다. */
export async function getMirrorMembers(params = {}) {
  const res = await api.get(`${MIRROR_BASE}/members`, { params: cleanParams(params) });
  return res.data;
}

/** 차량 미러 목록 — 허용된 스코프에 한해 번호판/회원 표시명만 반환한다. */
export async function getMirrorVehicles(params = {}) {
  const res = await api.get(`${MIRROR_BASE}/vehicles`, { params: cleanParams(params) });
  return res.data;
}

/** 세차 기록 — 분류별 카운트(breakdown) + 실 세차 수 + 목록(items). actual_wash 만 실세차다. */
export async function getMirrorWash(params = {}) {
  const res = await api.get(`${MIRROR_BASE}/wash`, { params: cleanParams(params) });
  return res.data;
}

/**
 * 일자별 세차 현황 — 일일(단일일)/일별(기간별) 공용.
 * data:{ days:[{date,breakdown,actualWash,washTotal,grossAmount,paymentCount}], totals, period, washDataState, paymentDataState }.
 * days 는 KST 날짜별(최신 우선). actual_wash 만 실세차.
 */
export async function getMirrorWashDaily(params = {}) {
  const res = await api.get(`${MIRROR_BASE}/wash-daily`, { params: cleanParams(params) });
  return res.data;
}

/** 결제 일별 요약 — 운영 참고치(재무 원장/네이티브 매출 아님). data.reference === true. */
export async function getMirrorPayments(params = {}) {
  const res = await api.get(`${MIRROR_BASE}/payments`, { params: cleanParams(params) });
  return res.data;
}

/** 활성 구독 — 서버가 master/manager 매장 스코프를 강제하고, 이름/번호판 검색·상태 필터를 적용한다. */
export async function getMirrorSubscriptions(params = {}) {
  const res = await api.get(`${MIRROR_BASE}/subscriptions`, { params: cleanParams(params) });
  return res.data;
}
